import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Box,
    Typography,
    Button,
    Divider,
    Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguageIcon from "@mui/icons-material/Language";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import themeColors from "../theme";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

// Parse a "YYYY-MM-DD" string as a local date (avoids timezone shifting the day).
function parseDate(value) {
    if (!value) return null;
    const [y, m, d] = String(value).split("-").map(Number);
    if (!y || !m || !d) return null;
    return { y, m: m - 1, d };
}

// Format a start/end pair like "January 4-22, 2027" or "December 30, 2026 - January 2, 2027".
function formatDateRange(start, end) {
    const s = parseDate(start);
    const e = parseDate(end);
    if (!s && !e) return "";
    if (!e) return `${MONTHS[s.m]} ${s.d}, ${s.y}`;
    if (!s) return `${MONTHS[e.m]} ${e.d}, ${e.y}`;

    if (s.y === e.y && s.m === e.m && s.d === e.d) {
        return `${MONTHS[s.m]} ${s.d}, ${s.y}`;
    }
    if (s.y === e.y && s.m === e.m) {
        return `${MONTHS[s.m]} ${s.d}-${e.d}, ${s.y}`;
    }
    if (s.y === e.y) {
        return `${MONTHS[s.m]} ${s.d} - ${MONTHS[e.m]} ${e.d}, ${s.y}`;
    }
    return `${MONTHS[s.m]} ${s.d}, ${s.y} - ${MONTHS[e.m]} ${e.d}, ${e.y}`;
}

function stripProtocol(url) {
    return String(url).replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function DetailRow({ icon: Icon, label, children, teal }) {
    return (
        <>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 2 }}>
                <Icon sx={{ color: teal, fontSize: 28, mt: 0.25, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                        {label}
                    </Typography>
                    <Box sx={{ fontSize: "1.05rem", fontWeight: 600, color: "#2C3E50", wordBreak: "break-word" }}>
                        {children}
                    </Box>
                </Box>
            </Box>
            <Divider />
        </>
    );
}

export default function ConferenceDetailModal({ conference, open, onClose }) {
    const { teal, darkTeal } = themeColors;

    if (!conference) return null;

    const isFree = !conference.price || conference.price === 0;
    const dateRange = formatDateRange(conference.startDate, conference.endDate);
    const registrationLink = conference.registrationLink || conference.purchaseLink;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
            <DialogTitle
                sx={{
                    background: `linear-gradient(135deg, ${teal}, ${darkTeal})`,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EventIcon />
                    <Typography variant="h6" fontWeight={600}>
                        Conference Details
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}>
                    {/* Conference Image */}
                    <Box
                        sx={{
                            width: { xs: "100%", md: 340 },
                            minHeight: 320,
                            backgroundColor: "#e8f0f2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 3,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={conference.image || "/images/books/book-default.png"}
                            alt={conference.name || conference.title}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/images/books/book-default.png";
                            }}
                            sx={{
                                maxWidth: "100%",
                                maxHeight: 380,
                                objectFit: "cover",
                                borderRadius: 2,
                                boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                            }}
                        />
                    </Box>

                    {/* Conference Info */}
                    <Box sx={{ flex: 1, p: 4, minWidth: 0 }}>
                        <Typography variant="h4" fontWeight={800} sx={{ color: teal, mb: 2 }}>
                            {conference.name || conference.title}
                        </Typography>

                        <Divider />

                        {dateRange && (
                            <DetailRow icon={CalendarMonthIcon} label="Date:" teal={teal}>
                                {dateRange}
                            </DetailRow>
                        )}

                        {conference.location && (
                            <DetailRow icon={LocationOnIcon} label="Location:" teal={teal}>
                                {conference.location}
                            </DetailRow>
                        )}

                        {conference.conferenceLink && (
                            <DetailRow icon={LanguageIcon} label="Link:" teal={teal}>
                                <Link
                                    href={conference.conferenceLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: teal, fontWeight: 600, textDecoration: "none" }}
                                >
                                    {stripProtocol(conference.conferenceLink)}
                                </Link>
                            </DetailRow>
                        )}

                        {/* Cost + Register */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                flexWrap: "wrap",
                                mt: 3,
                                p: 3,
                                backgroundColor: "#f8fafa",
                                borderRadius: 2,
                                border: "1px solid #e0e8ea",
                            }}
                        >
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Cost:
                                </Typography>
                                <Typography variant="h4" fontWeight={800} sx={{ color: teal }}>
                                    {isFree ? "Free" : `$${Number(conference.price).toFixed(2)}`}
                                </Typography>
                            </Box>

                            {registrationLink && (
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<ShoppingCartIcon />}
                                    onClick={() =>
                                        window.open(registrationLink, "_blank", "noopener,noreferrer")
                                    }
                                    sx={{
                                        background: `linear-gradient(135deg, ${teal}, ${darkTeal})`,
                                        color: "white",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "1rem",
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        boxShadow: `0 4px 15px ${teal}40`,
                                        "&:hover": {
                                            background: `linear-gradient(135deg, ${darkTeal}, ${teal})`,
                                            boxShadow: `0 6px 20px ${teal}60`,
                                        },
                                    }}
                                >
                                    Register here
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
